import { SECTION_CODE_BY_NAME } from "@frontend/common";

import {
  ClinicalTrials,
  EntryDataValueInfo,
} from "@/types/clinicalTrials.types";
import { removeStartBrackets } from "@utils/removeBrackets";

import { ArticleMainLabelList } from "./articleMainLabelList";
import { transformTextListFromEntryDataValue } from "../../../utils/transformTextListFromEntryDataValue";
import { MAIN_LABEL_VALUE_CLINICAL_TRIALS } from "../../constants/mainLabel.constants";
import {
  FormattedMainLabelListType,
  MainLabelArticleSectionCode,
  MainLabelEntityTypes,
  MainLabelFormatTypes,
  MainLabelInfoData,
  MainLabelListForUpdateType,
  MainLabelListType,
  ClinicalTrialsMainLabelNames,
  MainLabelNames,
} from "../../types/articleMainLabel.types";

export class ClinicalTrialsArticleMainLabelList extends ArticleMainLabelList {
  #targetSection = "Clinical Trials" as const;

  mainLabelList: MainLabelListType;

  mainLabelPathOfEntryData: Record<
    string,
    (data: { "Clinical Trials"?: Partial<ClinicalTrials> }) => string[]
  >;

  constructor() {
    super();
    this.mainLabelPathOfEntryData = MAIN_LABEL_VALUE_CLINICAL_TRIALS;
    this.mainLabelList = [];
  }

  public getArticleMainLabelList(): MainLabelListType {
    return this.mainLabelList;
  }

  // eslint-disable-next-line class-methods-use-this
  public getFormattedMainLabelList(
    defaultMainLabelList: MainLabelInfoData,
    mainLabelList: MainLabelListType,
  ): FormattedMainLabelListType {
    const formattedLabelList = defaultMainLabelList.map((labelInfo) => {
      const targetValueList = mainLabelList
        .filter((label) => label.labelName === labelInfo.labelName)
        .map((label) => label.labelValue);

      return {
        ...labelInfo,
        value: [...new Set(targetValueList)] || [],
      };
    });

    return formattedLabelList;
  }

  public createMainLabelList(
    defaultMainLabelList: MainLabelInfoData,
    entryData: {
      "Clinical Trials"?: Partial<ClinicalTrials>;
    },
  ): MainLabelListForUpdateType {
    const temp: MainLabelListForUpdateType = [];
    defaultMainLabelList.forEach((labelInfo) => {
      const targetValue =
        labelInfo.type === "MLT005"
          ? this.mainLabelPathOfEntryData[labelInfo.labelName](
              entryData,
            )?.filter((_, index) => index === 0)
          : this.mainLabelPathOfEntryData[labelInfo.labelName](entryData);
      if (targetValue) {
        targetValue.map((value) =>
          temp.push({
            ...labelInfo,
            value,
            id: "",
          }),
        );
      }
    });

    return temp;
  }

  public getTargetOptionList(
    labelName: MainLabelNames,
    entryData: {
      "Clinical Trials"?: Partial<ClinicalTrials>;
    },
  ): string[] {
    if (labelName === "Target Indication") {
      const targetIndication =
        entryData?.[this.#targetSection]?.["Drug Profile"]?.[0]?.[
          "Target Indication"
        ]?.value || [];
      return transformTextListFromEntryDataValue(targetIndication);
    }

    const CLINICAL_TRIALS_TARGET_VALUE: Record<
      ClinicalTrialsMainLabelNames,
      EntryDataValueInfo[] | []
    > = {
      Modality:
        entryData?.[this.#targetSection]?.[
          "Drug Profile"
        ]?.[0]?.Modality?.value?.map((item) => ({
          ...item,
          text: item.text.replace("(etc)", "").trim(),
        })) || [],
      Target:
        entryData?.[this.#targetSection]?.[
          "Drug Profile"
        ]?.[0]?.Target.value?.map((item) => {
          return {
            text: removeStartBrackets(item.text),
            is_modified: item.is_modified,
          };
        }) || [],
      "Drug Name": [],
      Phase: [],
      "Inclusion Criteria - Diagnostic":
        entryData?.[this.#targetSection]?.["Clinical Trial Design"]?.[0]?.[
          "Clinical Trial Design-1"
        ]?.["Inclusion Criteria - Diagnostic"]?.value || [],
      "Results Classification":
        entryData?.[this.#targetSection]?.["Clinical Results"]?.[
          "Results Classification"
        ].value || [],
    };

    const MAIN_LABEL_OPTION_LIST: Record<
      ClinicalTrialsMainLabelNames,
      string[]
    > = {
      Modality: CLINICAL_TRIALS_TARGET_VALUE.Modality
        ? transformTextListFromEntryDataValue(
            CLINICAL_TRIALS_TARGET_VALUE.Modality,
          )
        : [],
      Target: CLINICAL_TRIALS_TARGET_VALUE.Target
        ? transformTextListFromEntryDataValue(
            CLINICAL_TRIALS_TARGET_VALUE.Target,
          )
        : [],
      "Drug Name": [],
      Phase: [],
      "Inclusion Criteria - Diagnostic": CLINICAL_TRIALS_TARGET_VALUE[
        "Inclusion Criteria - Diagnostic"
      ]
        ? transformTextListFromEntryDataValue(
            CLINICAL_TRIALS_TARGET_VALUE["Inclusion Criteria - Diagnostic"],
          )
        : [],
      "Results Classification": CLINICAL_TRIALS_TARGET_VALUE[
        "Results Classification"
      ]
        ? transformTextListFromEntryDataValue(
            CLINICAL_TRIALS_TARGET_VALUE["Results Classification"],
          )
        : [],
    };

    return (
      MAIN_LABEL_OPTION_LIST[labelName as ClinicalTrialsMainLabelNames] || []
    );
  }

  public entryDataCompatibilityWithMainLabelsData(
    defaultMainLabelList: MainLabelInfoData,
    mainLabelList: MainLabelListType,
    entryData: {
      "Clinical Trials"?: Partial<ClinicalTrials>;
    },
  ): Record<ClinicalTrialsMainLabelNames, boolean> {
    const checkTargetMainLabelValue = (
      labelName: ClinicalTrialsMainLabelNames,
    ) => {
      return this.getFormattedMainLabelList(
        defaultMainLabelList,
        mainLabelList,
      )?.filter((label) => label.labelName === labelName)?.[0]?.value;
    };

    const CHECK_CLINICAL_TRIALS_TARGET_MAIN_LABEL: Record<
      ClinicalTrialsMainLabelNames,
      boolean
    > = {
      "Drug Name": !!(
        this.mainLabelPathOfEntryData["Drug Name"]?.(entryData)?.[0] ===
        checkTargetMainLabelValue("Drug Name")?.[0]
      ),
      Target:
        checkTargetMainLabelValue("Target")?.length === 0 ||
        checkTargetMainLabelValue("Target")?.filter((value) =>
          this.mainLabelPathOfEntryData.Target(entryData)?.includes(value),
        )?.length === checkTargetMainLabelValue("Target")?.length,
      Modality: this.mainLabelPathOfEntryData
        .Modality?.(entryData)
        .includes(checkTargetMainLabelValue("Modality")?.[0]),
      Phase: !!(
        this.mainLabelPathOfEntryData.Phase?.(entryData)?.[0] ===
        checkTargetMainLabelValue("Phase")?.[0]
      ),
      "Inclusion Criteria - Diagnostic": this.mainLabelPathOfEntryData[
        "Inclusion Criteria - Diagnostic"
      ]?.(entryData).includes(
        checkTargetMainLabelValue("Inclusion Criteria - Diagnostic")?.[0],
      ),
      "Results Classification": this.mainLabelPathOfEntryData[
        "Results Classification"
      ]?.(entryData).includes(
        checkTargetMainLabelValue("Results Classification")?.[0],
      ),
    };

    return CHECK_CLINICAL_TRIALS_TARGET_MAIN_LABEL;
  }

  public mainLabelListForEntryDataCompatibility(
    defaultMainLabelList: MainLabelInfoData,
    mainLabelList: MainLabelListType,
    entryData: {
      "Clinical Trials"?: Partial<ClinicalTrials>;
    },
  ): MainLabelListForUpdateType | null {
    const entryDataCompatibilityWithMainLabels =
      this.entryDataCompatibilityWithMainLabelsData(
        defaultMainLabelList,
        mainLabelList,
        entryData,
      );
    const isNeedUpdate =
      Object.values(entryDataCompatibilityWithMainLabels).every(
        (value) => value,
      ) === false;

    if (!isNeedUpdate) return null;

    const needToUpdateMainLabelKeys = Object.entries(
      entryDataCompatibilityWithMainLabels,
    )
      .map(([key, value]) => {
        if (value === false) return key;
        return null;
      })
      .filter((value) => value !== null) as MainLabelNames[];

    const formattedMainLabelList = this.getFormattedMainLabelList(
      defaultMainLabelList,
      mainLabelList,
    );

    const mainLabelListForEmptyValue = needToUpdateMainLabelKeys
      ?.filter((updatedLabel) => {
        const defaultLabels = mainLabelList?.map((label) => label.labelName);
        return defaultLabels.includes(updatedLabel) === false;
      })
      .map((label) => {
        const targetLabel = formattedMainLabelList.find(
          (item) => item.labelName === label,
        );
        if (!targetLabel) return null;
        return {
          id: "",
          labelName: label,
          type: targetLabel.type,
          articleSection: targetLabel.articleSection,
          entity: targetLabel.entity,
          labelValue: "",
        };
      })
      .filter((value) => value !== null);

    const newMainLabelList = [
      ...mainLabelList,
      ...mainLabelListForEmptyValue,
    ].map((label) => {
      if (label === null) return;
      if (
        label.labelName === "Target" &&
        needToUpdateMainLabelKeys.includes(label.labelName)
      ) {
        return {
          id: String(label.id),
          labelName: label.labelName,
          type: label.type,
          articleSection: label.articleSection,
          entity: label.entity,
          value: "",
        };
      }
      if (needToUpdateMainLabelKeys.includes(label.labelName)) {
        return {
          id: String(label.id),
          labelName: label.labelName,
          type: label.type,
          articleSection: label.articleSection,
          entity: label.entity,
          value:
            this.mainLabelPathOfEntryData[label.labelName]?.(entryData)?.[0],
        };
      }
      return {
        id: String(label.id),
        labelName: label.labelName,
        type: label.type,
        articleSection: label.articleSection,
        entity: label.entity,
        value: label.labelValue,
      };
    }) as {
      id: string;
      labelName: ClinicalTrialsMainLabelNames;
      type: MainLabelFormatTypes;
      articleSection: MainLabelArticleSectionCode;
      entity: MainLabelEntityTypes;
      value: string;
    }[];

    if (needToUpdateMainLabelKeys.includes("Target")) {
      const targetMainLabelData = this.mainLabelPathOfEntryData
        .Target?.(entryData)
        ?.map((value) => ({
          id: "",
          labelName: "Target" as ClinicalTrialsMainLabelNames,
          type: "MLT004" as MainLabelFormatTypes,
          articleSection:
            SECTION_CODE_BY_NAME.clinicalTrials as MainLabelArticleSectionCode,
          entity: "E020" as MainLabelEntityTypes,
          value,
        }));
      return [...newMainLabelList, ...targetMainLabelData];
    }

    return newMainLabelList.filter(
      (item) => !(item.id === "" && item.value === undefined),
    );
  }
}
